/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Input from '../../components/form-control/Input';
import Button from '../../components/form-control/Button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resetUserPassword } from '../../api/requests';
import { IResetPasswordDTO } from '../../resources/interfaces';
import { SIGN_IN } from '../../resources/links';
import useLayoutHook from '../../hooks/layoutHook';
import { IRequestResponse } from '../../resources/interfaces';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { showToast } from '../../utils/miscFunctions';

function ResetPassword() {
  const navigate = useNavigate();
  const { screenWidth } = useLayoutHook();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const userEmail = searchParams.get('email');
  const token = searchParams.get('ref');

  const submitHandler = (values: any) => {
    if (userEmail && token) {
      const payload: IResetPasswordDTO = {
        password: values.password,
        ref: token
      };

      setSubmitting(true);
      resetUserPassword(payload)
        .then((response: IRequestResponse) => {
          showToast.success(response?.message ?? 'Password reset successfully. Log in to continue.');
          navigate(SIGN_IN, { replace: true });
        })
        .catch((error: any) => {
          showToast.error(error.description);
        })
        .finally(() => setSubmitting(false));
    }
  };

  const usePasswordValidation = (password: string) => {
    return useMemo(() => ({
      hasMinLength: password.length >= 9,
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password),
    }), [password]);
  };

  const VALIDATION_MESSAGES = [
    { key: 'length', text: 'Must be at least 9 characters long' },
    { key: 'uppercase', text: 'Include at least one uppercase letter' },
    { key: 'number', text: 'Include at least one number' },
    { key: 'specialChar', text: 'Include at least one special character' }
  ];

  return (
    <div className="w-full">
      <Formik
        initialValues={{
          password: '',
          confirmPassword: ''
        }}
        validateOnChange={false}
        validateOnBlur={true}
        validationSchema={Yup.object({
          password: Yup.string()
            .trim()
            .required('Password is required')
            .min(9, 'A minimum of 9 characters required')
            .test({
              message: 'User email cannot be used as password',
              test: (value) => value !== userEmail
            })
            .matches(
              /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/,
              'Password must include at least two of the following: an uppercase character, a number, a special character'
            ),
          confirmPassword: Yup.string()
            .required('Password confirmation is required')
            .oneOf([Yup.ref('password'), null], 'Passwords must match')
        })}
        onSubmit={(values) => {
          submitHandler(values);
        }}>
        {({ values, errors, handleSubmit, handleChange, handleBlur }) => {
          const { hasMinLength, hasUppercase, hasNumber, hasSpecialChar } = usePasswordValidation(values.password);

          return (
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <h3 className="text-center text-base sm:text-lg font-medium text-gray-700">{userEmail}</h3>

              <div className="space-y-4">
                <Input
                  name="password"
                  label="Password"
                  value={values.password}
                  type="password"
                  wrapperClasses="w-full"
                  showPasswordIcon
                  required
                  error={errors.password}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  helperText={errors.password}
                  description="Must be more than 8 characters. Include an uppercase character, a number and a special character"
                  labelClasses='!text-gray-700'
                />

                {errors.password && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-1"
                  >
                    <div className="text-red-600 text-xs sm:text-sm space-y-1">
                      {[
                        !hasMinLength && VALIDATION_MESSAGES[0],
                        !hasUppercase && VALIDATION_MESSAGES[1],
                        !hasNumber && VALIDATION_MESSAGES[2],
                        !hasSpecialChar && VALIDATION_MESSAGES[3]
                      ]
                        .filter((msg): msg is { key: string; text: string } => Boolean(msg))
                        .map((msg, index) => (
                          <motion.div
                            key={msg.key}
                            className="flex items-start gap-2"
                            initial={{ x: -5, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <span className="mt-1">•</span>
                            <span className="text-left">{msg.text}</span>
                          </motion.div>
                        ))}
                    </div>
                  </motion.div>
                )}

                <Input
                  name="confirmPassword"
                  label="Confirm Password"
                  value={values.confirmPassword}
                  type="password"
                  wrapperClasses="w-full mt-6"
                  showPasswordIcon
                  required
                  error={errors.confirmPassword}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  helperText={errors.confirmPassword}
                  labelClasses='!text-gray-700'
                  />

                <div className="pt-2">
                  <Button
                    type="submit"
                    label="Set Password"
                    className="w-full flex justify-center items-center py-3 sm:py-3.5 px-4 text-sm sm:text-base font-medium text-white bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-900 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-600 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg rounded-lg"
                    loading={submitting}
                    disabled={submitting}
                  />
                </div>
              </div>
            </form>
          )
        }}
      </Formik>
    </div>
  );
}

export default ResetPassword;